"use client";

import { RefObject } from "react";
import { Button } from "@/components/ui/button";
import {
  Utensils,
  Globe2,
  Smartphone,
  Star,
  Coins,
  TrendingUp,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingOverlay } from "@/components/LoadingOverlay";
import MenuUpload from "@/components/MenuUpload";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { useLocale } from "next-intl";
import { trackEvent } from "@/lib/mixpanel-client";

export interface HeroSectionProps {
  menuUploadRef: RefObject<HTMLDivElement>;
  handleUploadComplete: () => void;
  handleProcessingStart: (progress: {
    fileUploaded: boolean;
    analyzing: boolean;
    extracting: boolean;
    creating: boolean;
  }) => void;
  handleProcessingComplete: () => void;
  showSignupPrompt: boolean;
  setShowSignupPrompt: (show: boolean) => void;
  isProcessingMenu: boolean;
  processingProgress: {
    fileUploaded: boolean;
    analyzing: boolean;
    extracting: boolean;
    creating: boolean;
  };
}

export const HeroSection = ({
  menuUploadRef,
  handleUploadComplete,
  handleProcessingStart,
  handleProcessingComplete,
  showSignupPrompt,
  setShowSignupPrompt,
  isProcessingMenu,
  processingProgress,
}: HeroSectionProps) => {
  // Get translations
  const t = useTranslations("HomePage");
  const navT = useTranslations("Navigation");
  const locale = useLocale();
  const isJapanese = locale === "ja";

  // Track signup initiated from hero section
  const handleSignupClick = () => {
    trackEvent("Signup Initiated", {
      Source: "Hero Upload Prompt",
    });
  };

  // Create a custom headline for English to prevent "to" from being isolated
  const renderHeadline = () => {
    if (locale === "en") {
      return (
        <>
          Make your menu{" "}
          <span className="whitespace-nowrap">accessible to</span>
          <br className="hidden md:block" />
          <span className="bg-gradient-to-r from-primary via-primary/80 to-primary text-transparent bg-clip-text block pb-2 text-balance">
            every customer
          </span>
        </>
      );
    }

    // For Japanese and other languages
    return (
      <span style={isJapanese ? { lineHeight: "1.25em" } : undefined}>
        {t("makeYourMenu")}
        {/* Only show line break on larger screens, not on iPad-sized devices */}
        <br className="hidden md:block" />
        <span className="bg-gradient-to-r from-primary via-primary/80 to-primary text-transparent bg-clip-text block pb-2 text-balance">
          {t("everyCustomer")}
        </span>
      </span>
    );
  };

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <section
      className="container mx-auto px-4 py-4 md:py-4 lg:py-8 relative"
      id="hero-section"
      aria-labelledby="hero-heading"
    >
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute top-0 left-0 right-0 h-[600px] bg-menu-paper opacity-30"></div>
        <div className="absolute top-[20%] right-[20%] w-[600px] h-[600px] bg-primary/10 blur-[128px] animate-pulse"></div>
        <div
          className="absolute top-[30%] left-[15%] w-[400px] h-[400px] bg-blue-500/10 blur-[128px] animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <motion.div
        className="flex flex-col items-center text-center max-w-4xl mx-auto relative"
        initial="hidden"
        animate="visible"
        variants={staggerChildren}
      >
        {/* Limited time offer banner */}
        <div className="flex flex-col items-center mb-2">
          <motion.p
            variants={fadeIn}
            className="text-xs text-muted-foreground mb-1"
          >
            {t("limitedTimeOffer")}
          </motion.p>
          <motion.div
            variants={fadeIn}
            className="inline-flex items-center rounded-full border border-border bg-background px-4 py-1.5 text-sm backdrop-blur-sm shadow-sm"
          >
            <span className="text-foreground font-medium font-sans flex items-center gap-1.5">
              <Coins className="h-4 w-4 text-gold-400" />
              <span>
                <span className="font-bold text-gold-500">50 FREE</span>{" "}
                {t("welcomeCredits")}
              </span>
            </span>
          </motion.div>
        </div>

        {/* Main headline with conditional rendering for different languages */}
        <motion.h1
          variants={fadeIn}
          className={`${
            isJapanese
              ? "text-3xl sm:text-4xl md:text-5xl"
              : "text-4xl sm:text-5xl md:text-6xl"
          } font-display font-[650] tracking-tight leading-[1.15] max-w-4xl text-pretty`}
          style={{ marginTop: 0 }}
          id="hero-heading"
        >
          {renderHeadline()}
        </motion.h1>

        <motion.p
          variants={fadeIn}
          className="text-lg md:text-xl text-muted-foreground max-w-2xl font-sans pt-0 text-balance"
        >
          {t("menuUploadDesc")}
        </motion.p>

        {/* Menu Upload Card with animation */}
        <motion.div
          variants={fadeIn}
          className="w-full max-w-lg mx-auto mt-8 relative perspective-1000"
          initial="hidden"
          animate="visible"
        >
          {/* Streamlined animated menu container */}
          <div className="relative h-[400px] w-full">
            {/* Front Cover - simple branded cover */}
            <motion.div
              className="absolute inset-0 z-20 bg-primary text-primary-foreground rounded-xl shadow-md flex flex-col items-center justify-center backface-hidden"
              initial={{ rotateY: 0 }}
              animate={{
                rotateY: 180,
                transition: {
                  delay: 1,
                  duration: 1.8,
                  ease: [0.4, 0.0, 0.2, 1],
                },
              }}
            >
              {/* Minimal cover content */}
              <motion.div
                className="flex flex-col items-center text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center justify-center w-20 h-20 rounded-full bg-primary-foreground text-primary mb-5">
                  <Utensils className="w-10 h-10" />
                </div>

                <h2 className="text-3xl font-accent font-semibold mb-3">
                  Taberu
                </h2>

                <h3 className="text-xl font-accent mb-3">
                  {t("menuTranslation")}
                </h3>
              </motion.div>
            </motion.div>

            {/* Back side - clean, focused upload area */}
            <motion.div
              className="absolute inset-0 backface-hidden bg-white rounded-xl shadow-md overflow-hidden"
              initial={{ rotateY: -180 }}
              animate={{
                rotateY: 0,
                transition: {
                  delay: 1,
                  duration: 1.8,
                  ease: [0.4, 0.0, 0.2, 1],
                },
              }}
            >
              {/* Content container with full dimensions */}
              <div className="relative h-full w-full flex items-center justify-center">
                <div className="w-full h-full p-6">
                  <div
                    className="w-full h-full flex flex-col"
                    ref={menuUploadRef}
                  >
                    <div className="flex-grow flex flex-col items-center justify-center">
                      <style jsx global>{`
                        /* Target only the outer dropzone container */
                        .dropzone-container {
                          height: 100% !important;
                          min-height: 240px !important;
                          width: 100% !important;
                          font-family: var(--font-accent) !important;
                        }

                        /* Target only the outer container's border - the first div */
                        .dropzone-container > div:first-child {
                          height: 100% !important;
                          width: 100% !important;
                          border: 2px dashed #e11d48 !important;
                          display: flex !important;
                          flex-direction: column !important;
                          justify-content: center !important;
                          align-items: center !important;
                          padding: 20px !important;
                          border-radius: 8px !important;
                          background-color: white !important;
                        }

                        /* Remove inner dotted border */
                        .dropzone-container > div > div > div {
                          border: none !important;
                        }

                        /* Improve text contrast */
                        .dropzone-container p,
                        .dropzone-container span {
                          color: #374151 !important;
                          font-weight: 500 !important;
                          font-family: var(--font-accent) !important;
                        }

                        /* Style the dropzone icon */
                        .dropzone-container svg {
                          color: #e11d48 !important;
                        }

                        /* Style buttons in the upload area */
                        .dropzone-container button {
                          font-family: var(--font-accent) !important;
                        }

                        /* Style the white button text */
                        .dropzone-container button svg.text-white {
                          color: white !important;
                        }

                        /* Ensure button text is white */
                        .dropzone-container button.bg-primary span,
                        .dropzone-container button.bg-primary {
                          color: white !important;
                        }

                        /* Style the Take Photo button */
                        .dropzone-container button[aria-label="Take Photo"] {
                          background-color: white !important;
                          border-color: rgba(225, 29, 72, 0.3) !important;
                          color: #e11d48 !important;
                        }

                        .dropzone-container
                          button[aria-label="Take Photo"]:hover {
                          background-color: rgba(225, 29, 72, 0.05) !important;
                        }
                      `}</style>
                      <MenuUpload
                        onUploadComplete={handleUploadComplete}
                        onProcessingStart={handleProcessingStart}
                        onProcessingComplete={handleProcessingComplete}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Signup Dialog */}
        <Dialog
          open={showSignupPrompt && !isProcessingMenu}
          onOpenChange={(open) => {
            if (!isProcessingMenu) {
              setShowSignupPrompt(open);
            } else {
              setShowSignupPrompt(false);
            }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">
                {t("readyToSeeTranslations")}
              </DialogTitle>
              <DialogDescription>
                {t("createAccountDescription")}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4 mt-4">
              <Link
                href="/signup"
                className="w-full"
                onClick={handleSignupClick}
              >
                <Button className="w-full font-accent" size="lg">
                  {t("createAccount")}
                </Button>
              </Link>
              <GoogleAuthButton
                mode="signup"
                className="w-full"
                pendingUploadId={menuUploadRef.current?.id || undefined}
              />
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border"></span>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-background px-2 text-muted-foreground">
                    {navT("login")}
                  </span>
                </div>
              </div>
              <Link href="/login" className="w-full">
                <Button variant="outline" className="w-full font-accent">
                  {t("alreadyHaveAccount")} {navT("login")}
                </Button>
              </Link>
            </div>
          </DialogContent>
        </Dialog>

        {/* Processing overlay */}
        {isProcessingMenu && <LoadingOverlay progress={processingProgress} />}

        {/* Trust Badges */}
        <motion.div variants={fadeIn} className="py-4 mt-12 w-full">
          <h2 className="text-sm font-medium text-muted-foreground mb-6 font-accent">
            {t("trustedBy")}
          </h2>
          <div className="flex flex-wrap justify-center gap-8 opacity-80">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Star className="w-4 h-4 text-yellow-500" />
              <span>{t("delightCustomers")}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <TrendingUp className="w-4 h-4 text-food-green" />
              <span>{t("growBusiness")}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Smartphone className="w-4 h-4 text-blue-500" />
              <span>{t("readyInMinutes")}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Globe2 className="w-4 h-4 text-violet-500" />
              <span>{t("languages")}</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
